/**
 * Soumission unique SearchBar / HomeNativeSearch / SimplifiedSearchBar.
 * Ask : mode=ask&intent=&profession=API&ask= — jamais q/query/seed.
 */

import { captureBookingEvent } from '../analytics/posthog';
import { BookingAnalyticsEvents } from '../analytics/events';
import { DEFAULT_PROFESSIONS, SIMPLE_PROFESSION_SYNONYMS } from './DefaultProfessions';
import { askHasProblemTokens, classifyAskIntent, normalizeAskText } from './classifyAskIntent';
import { resolveAskAnswer } from './askBlogMap';
import { BOOKING_LANDING_PATH, SEARCH_RESULTS_PATH } from '../config/site';
import {
  buildDiscoveryNavigationPath,
  buildProsDiscoveryParams,
  DEFAULT_DISCOVERY_RADIUS_KM,
  DISCOVERY_RADIUS_KM_OPTIONS,
  geocodeCity,
  inferProfessionSlugFromText,
  type BuildProsDiscoveryParamsArgs,
} from './prosDiscoverySearch';

export type SubmitDiscoveryOrAskInput = {
  searchQuoi: string;
  searchOu: string;
  selectedProfessionSlug: string | null;
  selectedServiceTypeId?: string | number | null;
  selectedServiceTypeName?: string | null;
  selectedSpeciesId?: string | number | null;
  lat: number;
  lon: number;
  currentLocationSearch: string;
  country?: string | null;
  askEnabled: boolean;
};

export type SubmitDiscoveryOrAskResult =
  | { status: 'need_location' }
  | { status: 'incomplete' }
  | { status: 'ok'; path: string; mode: 'search' | 'ask' };

function isBareProfessionQuery(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const normalized = normalizeAskText(trimmed);
  if (!normalized) return false;

  const exactName = DEFAULT_PROFESSIONS.some(p => normalizeAskText(p.name) === normalized);
  if (exactName) return true;

  if (SIMPLE_PROFESSION_SYNONYMS[normalized]) return true;

  const inferred = inferProfessionSlugFromText(trimmed);
  if (!inferred) return false;
  const wordCount = normalized.split(' ').filter(Boolean).length;
  return wordCount <= 3 && !askHasProblemTokens(trimmed);
}

function existingRadius(currentLocationSearch: string): number | null {
  const r = parseInt(new URLSearchParams(currentLocationSearch).get('radius') || '', 10);
  if (Number.isFinite(r) && (DISCOVERY_RADIUS_KM_OPTIONS as readonly number[]).includes(r)) {
    return r;
  }
  return null;
}

export function buildAskDiscoveryParams(args: {
  phrase: string;
  searchOu: string;
  lat: number;
  lon: number;
  currentLocationSearch: string;
  country?: string | null;
}): URLSearchParams {
  const classified = classifyAskIntent(args.phrase);
  const answer = resolveAskAnswer(args.phrase, classified);
  const params = new URLSearchParams();
  params.set('mode', 'ask');
  params.set('intent', answer.intent);
  if (answer.professionApiSlug) {
    params.set('profession', answer.professionApiSlug);
  }
  params.set('ask', args.phrase.trim());
  if (answer.blogSlug) {
    params.set('blog', answer.blogSlug);
  }
  if (args.searchOu.trim()) {
    params.set('address', args.searchOu.trim());
  }
  if (args.lat !== 0 && args.lon !== 0) {
    params.set('lat', String(args.lat));
    params.set('lon', String(args.lon));
    params.set(
      'radius',
      String(existingRadius(args.currentLocationSearch) ?? DEFAULT_DISCOVERY_RADIUS_KM)
    );
  }
  const prevCountry = new URLSearchParams(args.currentLocationSearch).get('country');
  const country = args.country || prevCountry;
  if (country && ['BE', 'NL', 'be', 'nl'].includes(country)) {
    params.set('country', country.toUpperCase());
  }
  return params;
}

export async function submitDiscoveryOrAsk(
  input: SubmitDiscoveryOrAskInput,
  geocode: typeof geocodeCity = geocodeCity
): Promise<SubmitDiscoveryOrAskResult> {
  const searchQuoi = input.searchQuoi.trim();
  const searchOu = input.searchOu.trim();
  const hasCoords = input.lat !== 0 && input.lon !== 0;
  const hasCity = Boolean(searchOu) || hasCoords;
  const structuredSelection = Boolean(
    input.selectedProfessionSlug ||
    (input.selectedServiceTypeId != null && String(input.selectedServiceTypeId).trim() !== '') ||
    input.selectedServiceTypeName?.trim()
  );

  const goSearch = async (): Promise<SubmitDiscoveryOrAskResult> => {
    if (!hasCity) return { status: 'need_location' };
    let lat = input.lat;
    let lon = input.lon;
    if (searchOu && !hasCoords) {
      const geo = await geocode(searchOu, input.country || 'fr');
      lat = geo?.[0] ?? 0;
      lon = geo?.[1] ?? 0;
    }
    const params = buildProsDiscoveryParams({
      searchQuoi,
      selectedProfessionSlug: input.selectedProfessionSlug,
      selectedServiceTypeId: input.selectedServiceTypeId,
      selectedServiceTypeName: input.selectedServiceTypeName,
      selectedSpeciesId: input.selectedSpeciesId,
      searchOu: searchOu || (hasCoords ? searchOu : ''),
      lat,
      lon,
      currentLocationSearch: input.currentLocationSearch,
      country: input.country,
    } as BuildProsDiscoveryParamsArgs);
    const path = buildDiscoveryNavigationPath(params);
    if (path === BOOKING_LANDING_PATH) return { status: 'incomplete' };
    captureBookingEvent(BookingAnalyticsEvents.SEARCH_OR_ASK_SUBMITTED, {
      mode: 'search',
      q_len: searchQuoi.length,
      has_profession: Boolean(params.get('profession')),
      has_geo: Boolean(params.get('lat')),
      inferred_intent: params.get('profession') || 'structured',
    });
    return { status: 'ok', path, mode: 'search' };
  };

  if (structuredSelection) {
    return goSearch();
  }

  if (!input.askEnabled) {
    if (!hasCity) return { status: 'need_location' };
    return goSearch();
  }

  // Localisation seule : tous les professionnels autour, sans métier ni prestation.
  if (!searchQuoi) {
    if (hasCity) return goSearch();
    return { status: 'incomplete' };
  }

  if (isBareProfessionQuery(searchQuoi) && hasCity) {
    return goSearch();
  }
  if (isBareProfessionQuery(searchQuoi) && !hasCity) {
    return { status: 'need_location' };
  }

  let lat = input.lat;
  let lon = input.lon;
  if (searchOu && !hasCoords) {
    const geo = await geocode(searchOu, input.country || 'fr');
    lat = geo?.[0] ?? 0;
    lon = geo?.[1] ?? 0;
  }

  const classified = classifyAskIntent(searchQuoi);
  const params = buildAskDiscoveryParams({
    phrase: searchQuoi,
    searchOu,
    lat,
    lon,
    currentLocationSearch: input.currentLocationSearch,
    country: input.country,
  });
  captureBookingEvent(BookingAnalyticsEvents.SEARCH_OR_ASK_SUBMITTED, {
    mode: 'ask',
    q_len: searchQuoi.length,
    has_profession: Boolean(params.get('profession')),
    has_geo: Boolean(params.get('lat')),
    inferred_intent: classified.intent,
  });
  return {
    status: 'ok',
    path: `${SEARCH_RESULTS_PATH}?${params.toString()}`,
    mode: 'ask',
  };
}

export { isBareProfessionQuery };
